import { apiBaseUrl, apiDelete, apiGet, apiPost } from "@/services/api/httpClient";
import type {
  BuildingFavorite,
  BuildingViewRecord,
  BuildingVisit,
  FavoriteUpsertPayload,
  HiddenMemoryTopic,
  VisitUpsertPayload
} from "@/types/exploration";

export function fetchBuildingVisits(signal?: AbortSignal): Promise<BuildingVisit[]> {
  return apiGet("/exploration/visits", signal);
}

export function markBuildingVisit(
  buildingId: string,
  payload: VisitUpsertPayload = {},
  signal?: AbortSignal
): Promise<BuildingVisit> {
  return apiPost(
    `/exploration/visits/${encodeURIComponent(buildingId)}`,
    payload,
    signal
  );
}

export function deleteBuildingVisit(
  buildingId: string,
  signal?: AbortSignal
): Promise<{ deleted: boolean }> {
  return apiDelete(`/exploration/visits/${encodeURIComponent(buildingId)}`, signal);
}

export function cancelBuildingVisit(
  buildingId: string,
  signal?: AbortSignal
): Promise<{ deleted: boolean }> {
  return apiPost(
    `/exploration/visits/${encodeURIComponent(buildingId)}/cancel`,
    undefined,
    signal
  );
}

export function fetchBuildingFavorites(signal?: AbortSignal): Promise<BuildingFavorite[]> {
  return apiGet("/exploration/favorites", signal);
}

export function fetchBuildingViews(
  limit = 30,
  signal?: AbortSignal
): Promise<BuildingViewRecord[]> {
  return apiGet(`/exploration/views?limit=${Math.max(1, Math.min(limit, 100))}`, signal);
}

export function favoriteBuilding(
  buildingId: string,
  payload: FavoriteUpsertPayload = {},
  signal?: AbortSignal
): Promise<BuildingFavorite> {
  return apiPost(
    `/exploration/favorites/${encodeURIComponent(buildingId)}`,
    payload,
    signal
  );
}

export function deleteBuildingFavorite(
  buildingId: string,
  signal?: AbortSignal
): Promise<{ deleted: boolean }> {
  return apiDelete(`/exploration/favorites/${encodeURIComponent(buildingId)}`, signal);
}

export function fetchHiddenMemoryTopics(
  params: { bbox?: string; theme?: string; limit?: number } = {},
  signal?: AbortSignal
): Promise<HiddenMemoryTopic[]> {
  const sp = new URLSearchParams();
  if (params.bbox) sp.set("bbox", params.bbox);
  if (params.theme) sp.set("theme", params.theme);
  if (params.limit !== undefined) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return apiGet(`/exploration/hidden-topics${qs ? "?" + qs : ""}`, signal);
}

export function fetchHiddenMemoryTopic(
  topicId: string,
  signal?: AbortSignal
): Promise<HiddenMemoryTopic> {
  return apiGet(`/exploration/hidden-topics/${encodeURIComponent(topicId)}`, signal);
}

const SESSION_KEY_STORAGE_KEY = "jc_session_key";

function sessionKey(): string {
  try {
    const existing = window.localStorage.getItem(SESSION_KEY_STORAGE_KEY);
    if (existing) return existing;
    const generated =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    window.localStorage.setItem(SESSION_KEY_STORAGE_KEY, generated);
    return generated;
  } catch {
    return "anonymous";
  }
}

export async function trackBuildingView(buildingId: string): Promise<void> {
  try {
    await fetch(
      `${apiBaseUrl}/exploration/views/${encodeURIComponent(buildingId)}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Session-Key": sessionKey()
        },
        credentials: "include"
      }
    );
  } catch {
    // 浏览埋点静默失败，不影响页面正常使用
  }
}
